'use client';

/**
 * Prompt Form Modal
 *
 * Create or edit custom prompts with liquid glass design:
 * - Title, description, category selection
 * - Full prompt content editor
 * - Variable management (add/remove {{placeholders}})
 * - Live preview of prompt with variables highlighted
 * - Validation and error handling
 */

import React, { useState, useEffect } from 'react';
import { X, Sparkles, Trash2, Eye, Info, AlertCircle } from 'lucide-react';
import type { PromptTemplate, PromptCategory, PromptVariable } from '@ctrl-enter/types';
import {
  PROMPT_CATEGORIES,
  PROMPT_CATEGORY_LABELS,
  PROMPT_CATEGORY_ICONS,
  extractVariableNames,
} from '@ctrl-enter/types';
import { cn } from '../utils/cn';
import { Dialog } from './dialog';
import { PromptCategoryIcon } from './prompt-category-icon';
import { Button } from './button';

type PromptFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (prompt: Omit<PromptTemplate, 'id' | 'createdAt' | 'updatedAt' | 'source'>) => void;
  editingPrompt?: PromptTemplate | null; // If provided, we're editing; otherwise creating
};

export const PromptFormModal: React.FC<PromptFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingPrompt,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<PromptCategory>('general');
  const [variables, setVariables] = useState<PromptVariable[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPreview, setShowPreview] = useState(false);

  // Initialize form when editing
  useEffect(() => {
    if (editingPrompt) {
      setTitle(editingPrompt.title);
      setDescription(editingPrompt.description || '');
      setContent(editingPrompt.content);
      setCategory(editingPrompt.category);
      setVariables(editingPrompt.variables || []);
    } else {
      // Reset form for creation
      setTitle('');
      setDescription('');
      setContent('');
      setCategory('general');
      setVariables([]);
    }
    setErrors({});
    setShowPreview(false);
  }, [editingPrompt, isOpen]);

  // Auto-detect variables in content
  useEffect(() => {
    const detectedVarNames = extractVariableNames(content);

    // Update variables in a single atomic operation to avoid race conditions
    setVariables(prev => {
      // Keep only variables that are still in the content
      const stillUsed = prev.filter(v => detectedVarNames.includes(v.name));

      // Find new variables that don't exist yet
      const existingVarNames = stillUsed.map(v => v.name);
      const newVarNames = detectedVarNames.filter(name => !existingVarNames.includes(name));

      // Add new variables if any
      if (newVarNames.length > 0) {
        const newVariables: PromptVariable[] = newVarNames.map(name => ({
          name,
          label: name
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' '),
          required: false,
        }));

        return [...stillUsed, ...newVariables];
      }

      // Return stillUsed only if it's different from prev
      return stillUsed.length === prev.length ? prev : stillUsed;
    });
  }, [content]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.length > 200) {
      newErrors.title = 'Title must be 200 characters or less';
    }

    if (description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    if (!content.trim()) {
      newErrors.content = 'Prompt content is required';
    } else if (content.length > 10000) {
      newErrors.content = 'Prompt content must be 10,000 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const promptData: Omit<PromptTemplate, 'id' | 'createdAt' | 'updatedAt' | 'source'> = {
      title: title.trim(),
      description: description.trim() || undefined,
      content: content.trim(),
      category,
      variables: variables.length > 0 ? variables : [],
      createdBy: editingPrompt?.createdBy,
    };

    onSave(promptData);
    onClose();
  };

  const handleUpdateVariable = (index: number, updates: Partial<PromptVariable>) => {
    setVariables(prev => prev.map((v, i) => (i === index ? { ...v, ...updates } : v)));
  };

  const handleRemoveVariable = (index: number) => {
    setVariables(prev => prev.filter((_, i) => i !== index));
  };

  const isEditing = !!editingPrompt;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      className="flex flex-col max-h-[90vh] overflow-hidden"
    >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-xl">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {isEditing ? 'Edit Prompt' : 'Create New Prompt'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {isEditing ? 'Update your custom prompt' : 'Build a reusable prompt template'}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 hover:glass-surface rounded-xl transition-all duration-200"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto"
        >
          <div className="p-6 space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-foreground mb-1.5">
                Title <span className="text-destructive">*</span>
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g., Generate Prior Authorization"
                className={cn(
                  'w-full px-3 py-2 text-sm text-foreground',
                  'glass-surface border border-border/50 rounded-xl',
                  'placeholder:text-muted-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1',
                  'transition-all duration-200',
                  errors.title ? 'border-destructive' : ''
                )}
                maxLength={200}
                autoFocus
              />
              {errors.title && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.title}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">{title.length}/200 characters</p>
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                Description <span className="text-muted-foreground text-xs">(optional)</span>
              </label>
              <input
                id="description"
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="e.g., Create a prior authorization request from medical records"
                className={cn(
                  'w-full px-3 py-2 text-sm text-foreground',
                  'glass-surface border border-border/50 rounded-xl',
                  'placeholder:text-muted-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1',
                  'transition-all duration-200',
                  errors.description ? 'border-destructive' : ''
                )}
                maxLength={500}
              />
              {errors.description && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.description}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {description.length}/500 characters
              </p>
            </div>

            {/* Category */}
            <div>
              <label
                htmlFor="category"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                Category <span className="text-destructive">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {PROMPT_CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={cn(
                      'px-3 py-2 text-left rounded-xl text-sm text-foreground',
                      'border transition-all duration-300',
                      'flex items-center gap-2',
                      category === cat
                        ? 'bg-primary/10 border-primary text-primary shadow-sm'
                        : 'glass-surface border-border/50 hover:glass-surface-hover'
                    )}
                  >
                    <PromptCategoryIcon iconName={PROMPT_CATEGORY_ICONS[cat]} size={16} />
                    <span>{PROMPT_CATEGORY_LABELS[cat]}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Prompt Content */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="content" className="block text-sm font-medium text-foreground">
                  Prompt Content <span className="text-destructive">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  <Eye className="h-3 w-3" />
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
              </div>
              <textarea
                id="content"
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder="Enter your prompt text here. Use {{variable_name}} for placeholders that will be filled in when using the prompt."
                rows={8}
                className={cn(
                  'w-full px-3 py-2 text-sm text-foreground font-mono',
                  'glass-surface border border-border/50 rounded-xl',
                  'placeholder:text-muted-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1',
                  'transition-all duration-200',
                  'resize-y',
                  errors.content ? 'border-destructive' : ''
                )}
                maxLength={10000}
              />
              {errors.content && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.content}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {content.length}/10,000 characters
              </p>

              {/* Variables help text */}
              <div className="mt-2 p-3 glass-surface-bg glass-surface-border rounded-xl">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-muted-foreground">
                    <p className="font-medium mb-1">Using Variables:</p>
                    <p>
                      Type{' '}
                      <code className="px-1 py-0.5 glass-surface-bg glass-surface-border rounded">
                        {'{{variable_name}}'}
                      </code>{' '}
                      in your prompt to create placeholders. Variables will be auto-detected and you
                      can configure them below.
                    </p>
                    <p className="mt-1">
                      Example:{' '}
                      <code className="px-1 py-0.5 glass-surface-bg glass-surface-border rounded">
                        {'Check {{patient_name}} for {{procedure}}'}
                      </code>
                    </p>
                  </div>
                </div>
              </div>

              {/* Preview */}
              {showPreview && content.trim() && (
                <div className="mt-3 p-4 glass-surface-bg glass-surface-border rounded-xl">
                  <div className="text-xs font-semibold text-muted-foreground mb-2">PREVIEW</div>
                  <pre className="text-sm text-foreground whitespace-pre-wrap font-mono">
                    {content}
                  </pre>
                </div>
              )}
            </div>

            {/* Variables Configuration */}
            {variables.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-foreground">
                    Variables ({variables.length})
                  </label>
                </div>
                <div className="space-y-3">
                  {variables.map((variable, index) => (
                    <div
                      key={variable.name}
                      className="p-3 glass-surface-bg glass-surface-border rounded-xl space-y-2 border border-border/50"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-foreground font-mono">
                            {'{{' + variable.name + '}}'}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveVariable(index)}
                          className="p-1 hover:bg-destructive/10 rounded-lg text-destructive transition-all duration-200"
                          title="Remove variable"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">
                            Display Label
                          </label>
                          <input
                            type="text"
                            value={variable.label}
                            onChange={e => handleUpdateVariable(index, { label: e.target.value })}
                            placeholder="Patient Name"
                            className={cn(
                              'w-full px-2 py-1 text-xs text-foreground',
                              'glass-surface border border-border/50 rounded-lg',
                              'focus:outline-none focus:ring-1 focus:ring-primary/30',
                              'transition-all duration-200'
                            )}
                          />
                        </div>

                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">
                            Placeholder
                          </label>
                          <input
                            type="text"
                            value={variable.placeholder || ''}
                            onChange={e =>
                              handleUpdateVariable(index, { placeholder: e.target.value })
                            }
                            placeholder="e.g., John Doe"
                            className={cn(
                              'w-full px-2 py-1 text-xs text-foreground',
                              'glass-surface border border-border/50 rounded-lg',
                              'focus:outline-none focus:ring-1 focus:ring-primary/30',
                              'transition-all duration-200'
                            )}
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`required-${index}`}
                          checked={variable.required || false}
                          onChange={e =>
                            handleUpdateVariable(index, { required: e.target.checked })
                          }
                          className="h-4 w-4 rounded border-border"
                        />
                        <label
                          htmlFor={`required-${index}`}
                          className="text-xs text-foreground cursor-pointer"
                        >
                          Required field
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-border/50">
            <div className="text-xs text-muted-foreground">
              {variables.length > 0 && (
                <span>
                  {variables.length} variable{variables.length !== 1 ? 's' : ''} detected
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button type="button" onClick={onClose} variant="outline" size="md">
                Cancel
              </Button>
              <Button type="submit" variant="primary" size="md">
                <Sparkles className="h-4 w-4" />
                <span>{isEditing ? 'Update Prompt' : 'Create Prompt'}</span>
              </Button>
            </div>
          </div>
        </form>
    </Dialog>
  );
};
